import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Col, Row } from "react-bootstrap";
import { Visibility } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import UndoIcon from "@mui/icons-material/Undo";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import LoadingSpinner from "components/LoadingSpinner";
import { showCustomSwal, showLoading, hideLoading } from "helpers/swalHelper";
import usePermission from "hooks/usePermission";
import { app_modules } from "helpers/apps_modules_list";
import { createSelector } from "reselect";
import {
  fetchAllInwards,
  revertInward,
  fetchCompletedReturnedAccessoriesDropdown,
  exportCompletedRAGRNCSV
} from "slices/Warehouse/ItemInward/ReturnedAccessories/thunk";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import ExportButton from "Common/Components/Buttons/ExportButton";

import { SortingState } from "@tanstack/react-table";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";

type Props = {
  warehouseId: string;
};

const ReturnedAccessoriesGRNTab: React.FC<Props> = ({ warehouseId }) => {
  const dispatch = useDispatch<any>();
  const location = useLocation();

  const [tableInstance, setTableInstance] = useState<any>();
  const [revertingId, setRevertingId] = useState<string | null>(null);

  const tableState = usePreservedTableState({
    key: `completedReturnedAccessoriesGRNTable-${warehouseId || 'all'}`,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  const { canView, canCreate, canUpdate, canDelete } = usePermission(
    app_modules?.warehouse?.app_name,
    app_modules?.warehouse?.modules?.item_inward,
  );

  const { allInwards, loading, rowCount } = useSelector(
    createSelector(
      (state: any) => state.returnedAccessoriesInward,
      (state) => ({
        allInwards: Array.isArray(state.allInwards) ? state.allInwards : [],
        loading: !!state.allInwardsLoading,
        rowCount: state.allInwardsCount || 0,
      }),
    )
  );

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  useClearStateOnNavigation(location, resetState);

  const fetchDropdown = async (field: string) => {
    const result = await dispatch(fetchCompletedReturnedAccessoriesDropdown({ field, warehouseId }));
    return result.payload?.data || [];
  };

  useEffect(() => {
    dispatch(fetchAllInwards({
      warehouseId,
      page: pageIndex + 1,
      limit: pageSize,
      search,
      sort: sortField,
      order: sortField ? sortDirection : "",
      filters: filterValue
    }));
  }, [dispatch, warehouseId, pageIndex, pageSize, search, sortField, sortDirection, filterValue]);

  const handleRevert = (inwardId: string) => {
    showCustomSwal({
      title: "Are you sure?",
      text: "You want to revert this GRN!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, revert it!",
      cancelButtonText: "Cancel",
    }).then((result: any) => {
      if (result.isConfirmed) {
        setRevertingId(inwardId);
        showLoading("Reverting...");
        dispatch(revertInward(inwardId))
          .unwrap()
          .then((res: any) => {
            hideLoading();
            setRevertingId(null);
            showCustomSwal({
              title: "Reverted!",
              text: "GRN has been reverted.",
              icon: "success",
            });
            dispatch(fetchAllInwards(warehouseId));
          })
          .catch((error: any) => {
            hideLoading();
            setRevertingId(null);
            console.error("Failed to revert", error);
            showCustomSwal({
              title: "Error",
              text: error.message || error.error || "Failed to revert GRN",
              icon: "error",
            });
          });
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        header: "GRN No.",
        accessorKey: "ragrn_no",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.WAREHOUSE?.ITEM_INWARD?.RETURNED_ACCESSORIES_GRN?.FILTER_DROPDOWN('ragrn_no', warehouseId)}`,
        meta: { filterType: "dropdown", fetchDropdownValues: () => fetchDropdown("ragrn_no") },
      },
      {
        header: "Challan No.",
        accessorKey: "challan.challan_no",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.WAREHOUSE?.ITEM_INWARD?.RETURNED_ACCESSORIES_GRN?.FILTER_DROPDOWN('challan_no', warehouseId)}`,
        meta: { filterType: "dropdown", fetchDropdownValues: () => fetchDropdown("challan_no") },
      },
      {
        header: "Received Date",
        accessorKey: "received_date",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "date" as const,
        cell: ({ row }: any) => <FormattedDate date={row.original.received_date} />,
      },
      {
        header: "Received By",
        accessorKey: "received_by",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.WAREHOUSE?.ITEM_INWARD?.RETURNED_ACCESSORIES_GRN?.FILTER_DROPDOWN('received_by', warehouseId)}`,
        meta: { filterType: "dropdown", fetchDropdownValues: () => fetchDropdown("received_by") },
      },
      {
        header: "Created By",
        accessorKey: "created_at",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "date" as const,
        meta: { filterType: "dropdown", fetchDropdownValues: () => fetchDropdown("created_by_user__first_name") },
        cell: ({ row }: any) =>
          row.original.created_by_user ? (
            <NameWithDate
              name={
                row.original.created_by_user.first_name ||
                row.original.created_by_user.username
              }
              date={row.original.created_at}
            />
          ) : (
            "—"
          ),
      },
      {
        header: "Updated By",
        accessorKey: "updated_at",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "date" as const,
        meta: { filterType: "dropdown", fetchDropdownValues: () => fetchDropdown("updated_by_user__first_name") },
        cell: ({ row }: any) =>
          row.original.updated_by_user ? (
            <NameWithDate
              name={
                row.original.updated_by_user.first_name ||
                row.original.updated_by_user.username
              }
              date={row.original.updated_at}
            />
          ) : (
            "—"
          ),
      },
      {
        header: "Action",
        enableSorting: false,
        cell: ({ row }: any) => {
          const d = row.original;
          const menuItems = [
            {
              icon: Visibility,
              label: "View",
              permission: canView,
              link: () => `/warehouse/item-inward/returned-accessories/grn/${d.inward_id}`,
              color: "primary",
            },
            {
              icon: EditIcon,
              label: "Edit",
              permission: canUpdate,
              link: () => `/warehouse/item-inward/returned-accessories/grn/${d.inward_id}/edit`,
              color: "primary",
            },
            {
              icon: UndoIcon,
              label: revertingId === d.inward_id ? "Reverting..." : "Revert",
              permission: canUpdate,
              disabled: revertingId === d.inward_id,
              onClick: () => {
                if (revertingId !== d.inward_id) handleRevert(d.inward_id);
              },
              color: "warning",
            },
          ].filter((item) => item.permission);
          return <DynamicMenuItem row={d} menuItems={menuItems} />;
        },
      },
    ],
    [canView, canUpdate, canDelete, dispatch, warehouseId, revertingId],
  );

  const filteredAndSortedData = useMemo(() => allInwards, [allInwards]);
  const paginatedData = useMemo(() => allInwards, [allInwards]);

  return (
    <div
      className="d-flex flex-column"
      style={{ flex: "1 1 auto", minHeight: 0, maxHeight: "100%", overflow: "hidden" }}
    >
      <div className="flex-shrink-0">
        <Row className="align-items-center mecord-search-row g-2">
          <SearchBox
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
            placeholder="Search GRN no., challan, received by..."
          />
          <Col xs={12} md={12} lg={8} xl={7} className="d-flex justify-content-end align-items-center gap-2">
            {tableInstance && (
              <ColumnVisibilityDropdown
                columns={tableInstance.getAllLeafColumns()}
                excludedColumns={["action"]}
              />
            )}
            {canView && (
              <ExportButton
                onExport={async () => {
                  return await exportCompletedRAGRNCSV({
                    warehouse_id: warehouseId || "",
                    search,
                    filters: filterValue,
                    sort: sorting[0]?.id,
                    order: sorting[0]?.desc ? "desc" : "asc",
                  });
                }}
              />
            )}
          </Col>
        </Row>
      </div>

      <div
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          maxHeight: "100%",
          marginTop: 4,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <LoadingSpinner />
        ) : (
          <MasterTableContainer
            columns={columns}
            data={paginatedData || []}
            totalRecords={rowCount}
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPageChange={setPageIndex}
            onPageSizeChange={setPageSize}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            colValue={setFilterValue}
            sorting={sorting}
            onSortingChange={setSorting}
            manualSorting={true}
            isPagination={true}
            onTableReady={setTableInstance}
          />
        )}
      </div>
    </div>
  );
};

export default ReturnedAccessoriesGRNTab;
